import PromotionsAndPricing from '@components/Product/Details/_PromotionsAndPricing';
import Markdown from '@repo/ui/src/components/Markdown';
import CallToActionButton from '@components/Product/CallToActionButton';
import CompatibleProducts from '@components/Product/Details/_CompatibleProducts';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import { useProductContext } from '@context/ProductProvider';
import Product from '@models/product';
import { LinkProps } from '@repo/ui/src/types/link';
import { ProductVariation } from '@typings/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import { PDPTemplate, isTierOne, isTierTwo } from '@views/ProductDetailPage/_enums';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import AlternateSizes from '../AlternateGrillSizes/New';
import AdditionalInformation from './_AdditionalInformation';
import LocallyDropdown from '@components/Product/_LocallyDropdown';
import TierTwoVariations from '@components/Product/Details/_TierTwoVariations';
import TierThreeVariations from '@components/Product/Details/_TierThreeVariations';
import { useVipAccess } from '@hooks/customer';
import { ErrorState } from '@repo/ui/src/components/Form';
import PurchaseOptions, { PurchaseOptionsProps } from './_PurchaseOptions';

export interface ProductColor {
  color: string;
  link: LinkProps;
  sku: string;
}

export interface AlternateColorProps {
  colors: Array<ProductColor>;
}

interface Props {
  template: PDPTemplate;
  dark?: boolean;
  promoMarkdown?: string;
  alternateSize?: Product;
  alternateColors?: AlternateColorProps;
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
  compatibleWith?: {
    image: any;
    link?: LinkProps;
  };
  purchaseOptions?: PurchaseOptionsProps;
}

const TierProductSelection = ({ template, ownerManual, prop65, dark, purchaseOptions }: Props) => {
  const { t } = useTranslation();

  const {
    totalPrice: _totalPrice,
    addToCart,
    addVariationToCart,
    isLoadingAddToCart,
    isLoading,
    mainProduct: product,
  } = useProductContext();
  const { vip } = useVipAccess();

  const [quantity, setQuantity] = React.useState(1);
  const [activeVariation, setActiveVariation] = React.useState<ProductVariation>();

  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + (item?.price ?? 0), 0);
  }, [_totalPrice, product]);

  if (!product) {
    return null;
  }

  const maxQuantity = Math.max(
    0,
    Math.min(activeVariation?.inventory?.stock_level ?? product.inventory?.stock_level ?? 99, 99),
  );
  const isQuantityValid = quantity >= 1 && quantity <= maxQuantity;

  const showPrice = !product.purchasing?.hideCTA;
  const shouldRenderLocally =
    (product.shop?.upc || activeVariation?.upc) &&
    !product.shop.disableLocally &&
    showPrice &&
    !product?.purchasing?.traegerExclusive;

  const showVipDisclaimer = vip && !product.isOrderable && !product.purchasing?.traegerExclusive;

  return (
    <div>
      <div className="grid gap-y-8">
        <ProductHeading
          dark={dark}
          className="md:block hidden"
          hideDescription
          hideSKU={template === PDPTemplate.TierThree}
        />
        <div className="hidden md:grid gap-y-8">
          {showPrice && (
            <PromotionsAndPricing
              dark={dark}
              className={template === PDPTemplate.TierThree ? 'hidden sm:flex' : 'flex'}
            />
          )}
          {product.shortDescription && (
            <div className="pt-4">
              <Markdown bodySize="xLarge" className={classNames(dark && 'text-white')}>
                {product.shortDescription}
              </Markdown>
            </div>
          )}
        </div>

        {isTierOne(template) && product.variations.length > 1 && (
          <AlternateSizes
            sizes={product.variations.filter((v) => v.productUrl)}
            activeProduct={product}
            dark={dark}
          />
        )}

        {isTierTwo(template) && <TierTwoVariations product={product} />}

        {template === PDPTemplate.TierThree && (
          <TierThreeVariations
            product={product}
            activeVariation={activeVariation}
            setActiveVariation={setActiveVariation}
          />
        )}

        <div className="w-full flex flex-col gap-y-6">
          {template === PDPTemplate.TierThree && !isLoading && (
            <div className="flex items-stretch" data-product-cta>
              <CallToActionButton
                // @ts-expect-error product !== variation
                product={
                  activeVariation ? { ...activeVariation, purchasing: product.purchasing } : product
                }
                disable={!activeVariation || isLoadingAddToCart || !isQuantityValid}
                buttonProcessing={isLoadingAddToCart}
                processingText=""
                customProductPrice={totalPrice}
                buttonProps={{
                  disabled: !activeVariation || isLoadingAddToCart || !isQuantityValid,
                  full: true,
                  tabIndex: 0,
                  className: isLoadingAddToCart ? 'disabled:bg-brand-300' : 'disabled:bg-warm-400',
                }}
                onClickAddToCart={() =>
                  activeVariation && addVariationToCart(activeVariation, quantity)
                }
              />
            </div>
          )}

          {isTierTwo(template) && showPrice && !isLoading && (
            <div className="flex items-stretch" data-product-cta>
              {product.isOrderable && maxQuantity > 0 && (
                <div className="flex flex-grow mr-4">
                  <div className="aspect-square w-15 border-neutral-300 border-2 bg-white flex align-center p-1 rounded-md">
                    <label className="tra-text-web-body-m text-neutral-500 text-center">
                      {t('productDetailPageContent.quantityInputLabel', undefined, {
                        default: 'Qty',
                      })}
                      <input
                        min={1}
                        max={maxQuantity}
                        type="number"
                        value={quantity}
                        className="text-neutral-600 justify-center w-full pt-2 text-center"
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          setQuantity(
                            isNaN(parsed) || parsed < 1 ? 1 : Math.min(parsed, maxQuantity),
                          );
                        }}
                        data-testid="quantityInput"
                      />
                    </label>
                  </div>
                </div>
              )}
              <CallToActionButton
                product={product}
                buttonProcessing={isLoadingAddToCart}
                processingText=""
                customProductPrice={totalPrice}
                buttonProps={{
                  disabled: isLoadingAddToCart || !isQuantityValid,
                  full: true,
                  tabIndex: 0,
                  className: isLoadingAddToCart ? 'disabled:bg-brand-300' : 'disabled:bg-warm-400',
                }}
                onClickAddToCart={() => {
                  addToCart(quantity);
                }}
              />
            </div>
          )}

          {isTierOne(template) && !isLoading && (
            <div className={classNames('flex items-stretch', showPrice && 'mb-5')} data-product-cta>
              <CallToActionButton
                product={product}
                buttonProcessing={isLoadingAddToCart}
                processingText=""
                customProductPrice={totalPrice}
                buttonProps={{
                  disabled: isLoadingAddToCart,
                  full: true,
                  tabIndex: 0,
                  className: isLoadingAddToCart ? 'disabled:bg-brand-300' : 'disabled:bg-warm-400',
                }}
                onClickAddToCart={() => addToCart(quantity)}
              />
            </div>
          )}

          {showVipDisclaimer && (
            <ErrorState
              text={t('productDetailPageContent.vipDiscountNotApplicable', undefined, {
                default:
                  "This product isn't available for VIP purchase. You can still find it at full price through our trusted retail partners.",
              })}
            >
              {}
            </ErrorState>
          )}

          {shouldRenderLocally && (
            <LocallyDropdown
              upc={activeVariation?.upc ?? product.shop.upc!}
              className={classNames(template !== PDPTemplate.TierThree && dark && 'locally-dark')}
            />
          )}
        </div>
      </div>

      <div className="pt-8 flex gap-5 flex-col">
        {product?.compatibleWith && (
          <CompatibleProducts dark={dark} compatibleWith={product.compatibleWith} />
        )}
        {purchaseOptions && <PurchaseOptions purchaseOptions={purchaseOptions} />}
        {isTierOne(template) && (
          <AdditionalInformation
            dark={dark}
            ownerManual={ownerManual}
            prop65={prop65}
            showGrillAssistanceInfo
          />
        )}
        {!isTierOne(template) && prop65 && <AdditionalInformation prop65={prop65} />}
      </div>
    </div>
  );
};

export default TierProductSelection;
